import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["children", "label", "interaction", "isOutsideMonth", "isSelected", "isToday", "onClick", "onKeyDown", "as", "styles"];
var _dec, _dec2, _class, _Day;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { View } from '@instructure/ui-view';
import { AccessibleContent } from '@instructure/ui-a11y-content';
import { omitProps, callRenderProp, getElementType } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
parent: Calendar
id: Calendar.Day
---
 **/
let Day = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_Day = class Day extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleClick = event => {
      const _this$props = this.props,
        onClick = _this$props.onClick,
        date = _this$props.date;
      if (typeof onClick === 'function') {
        onClick(event, {
          date
        });
      }
    };
    this.handleKeyDown = event => {
      const _this$props2 = this.props,
        onKeyDown = _this$props2.onKeyDown,
        date = _this$props2.date;
      if (typeof onKeyDown === 'function') {
        onKeyDown(event, {
          date
        });
      }
    };
    this.handleElementRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props3;
    (_this$props$makeStyle = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props3, this.makeStylesVariables);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props4;
    (_this$props$makeStyle2 = (_this$props4 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props4, this.makeStylesVariables);
  }
  get makeStylesVariables() {
    return {
      isDisabled: this.isDisabled
    };
  }
  get isDisabled() {
    const interaction = this.props.interaction;
    return interaction === 'disabled';
  }
  get elementType() {
    const as = this.props.as;
    return as || getElementType(Day, this.props);
  }
  shouldApplyAriaSelected() {
    const role = this.props.role;
    return !!role && ['option', 'gridcell'].indexOf(role) > -1;
  }
  render() {
    const _this$props5 = this.props,
      children = _this$props5.children,
      label = _this$props5.label,
      interaction = _this$props5.interaction,
      isOutsideMonth = _this$props5.isOutsideMonth,
      isSelected = _this$props5.isSelected,
      isToday = _this$props5.isToday,
      onClick = _this$props5.onClick,
      onKeyDown = _this$props5.onKeyDown,
      as = _this$props5.as,
      styles = _this$props5.styles,
      props = _objectWithoutProperties(_this$props5, _excluded);
    const elementType = this.elementType,
      isDisabled = this.isDisabled;
    const passthroughProps = View.omitViewProps(omitProps(props, Day.allowedProps), Day);
    return _jsx(View, {
      ...passthroughProps,
      as: elementType,
      css: styles === null || styles === void 0 ? void 0 : styles.calendarDay,
      display: "inline-block",
      margin: "xxx-small",
      borderWidth: "none",
      borderColor: "transparent",
      background: "transparent",
      cursor: elementType === 'button' || elementType === 'a' ? isDisabled ? 'not-allowed' : 'pointer' : 'auto',
      disabled: isDisabled,
      "aria-current": isToday ? 'date' : void 0,
      "aria-selected": this.shouldApplyAriaSelected() ? isSelected ? 'true' : 'false' : void 0,
      onClick: onClick && this.handleClick,
      onKeyDown: onKeyDown && this.handleKeyDown,
      elementRef: this.handleElementRef,
      children: _jsx("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.day,
        children: _jsx(AccessibleContent, {
          alt: label,
          children: callRenderProp(children)
        })
      })
    });
  }
}, _Day.displayName = "Day", _Day.componentId = 'Calendar.Day', _Day.propTypes = propTypes, _Day.allowedProps = allowedProps, _Day.defaultProps = {
  interaction: 'enabled',
  isSelected: false,
  isToday: false,
  isOutsideMonth: false
}, _Day)) || _class) || _class);
export default Day;
export { Day };